import { toast } from 'sonner';
import moment from 'moment-timezone';
import { ENDPOINTS, ORDER_TYPES } from '../../../helpers/constants';
import { makeRESTApiRequest } from '../../../helpers/api';

const toMoneyNumber = value => {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const roundMoney = n => Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

const sumAccountBalances = (rows, balanceKey) =>
  roundMoney((rows || []).reduce((sum, row) => sum + toMoneyNumber(row?.[balanceKey]), 0));

/** Sum of regular + delinquent account balances for tangible or business taxes. */
export const computeTaxSectionAmountDue = (taxes, kind) => {
  if (!taxes || (kind !== 'tangible' && kind !== 'business')) return 0;
  const regular = sumAccountBalances(taxes[`${kind}TaxDetails`], `${kind}TaxBalance`);
  const delinquent =
    taxes[`${kind}DelinquentTax`] === 'Yes'
      ? sumAccountBalances(taxes[`${kind}DelinquentTaxDetails`], `${kind}DelinquentTaxBalance`)
      : 0;
  return roundMoney(regular + delinquent);
};

export const TAX_BALANCE_SUBFIELD_KEYS = new Set([
  'tangibleTaxBalance',
  'tangibleDelinquentTaxBalance',
  'businessTaxBalance',
  'businessDelinquentTaxBalance'
]);

/** Recalculate Total Amount Due after tax account balance rows change. */
export const applyTaxAmountDueFromBalances = (sectionKey, section, detailsFieldKey) => {
  if (sectionKey !== 'taxes' || !detailsFieldKey) return section;
  const kind = detailsFieldKey.startsWith('tangible')
    ? 'tangible'
    : detailsFieldKey.startsWith('business')
      ? 'business'
      : null;
  if (!kind) return section;
  return {
    ...section,
    [`${kind}TaxAmountDue`]: String(computeTaxSectionAmountDue(section, kind))
  };
};

export const computeSuggestedInvoiceAmount = (hardCost, processingFee, minimumInvoicableAmount) =>
  roundMoney(Math.max(toMoneyNumber(hardCost) + toMoneyNumber(processingFee), toMoneyNumber(minimumInvoicableAmount)));

export const getTotalInvoiceAmount = invoices =>
  roundMoney((invoices || []).reduce((sum, row) => sum + toMoneyNumber(row?.invoiceAmount), 0));

export const isInvoiceTotalBelowSuggested = invoicesSection =>
  getTotalInvoiceAmount(invoicesSection?.invoices) < roundMoney(toMoneyNumber(invoicesSection?.suggestedInvoiceAmount));

export const getInvoiceBalanceDue = row =>
  (Number(row?.invoiceAmount) || 0) - (Number(row?.paymentReceived) || 0);

export const applyInvoiceRowRules = row => {
  const next = { ...row };
  if (getInvoiceBalanceDue(next) > 0) {
    next.status = 'Unpaid';
  }
  return next;
};

export const handleInvoiceMultiFieldChange = ({ field, fieldKey, subFieldKey, row }) => {
  if (field !== 'invoices' || fieldKey !== 'invoices') return row;
  if (!['invoiceAmount', 'paymentReceived'].includes(subFieldKey)) return row;
  return applyInvoiceRowRules(row);
};

export const INVOICE_STATUS_OPTIONS = ['Unpaid', 'Paid'];

export const EMPTY_INVOICE_ROW = {
  invoiceNumber: '',
  invoiceAmount: '',
  paymentReceived: '',
  invoiceDueDate: '',
  status: 'Unpaid'
};

/** Shared multi-field column config for report invoice rows (lien / estoppel / survey). */
export const getInvoiceMultiFields = ({ isFilePopupOpen = false } = {}) => {
  const rowCols = isFilePopupOpen
    ? { cellSize: 2, minWidth: 150 }
    : { cellSize: 12, cellSizeMd: 1, minWidth: 150 };
  const dueDateCols = isFilePopupOpen
    ? { cellSize: 2, minWidth: 150 }
    : { cellSize: 12, cellSizeMd: 1, minWidth: 150 };
  const statusCols = isFilePopupOpen
    ? { cellSize: 2, minWidth: 150 }
    : { cellSize: 12, cellSizeMd: 2, minWidth: 150 };

  return [
    {
      label: 'Invoice Number',
      key: 'invoiceNumber',
      type: 'text',
      ...rowCols
    },
    {
      label: 'Invoice Amount',
      key: 'invoiceAmount',
      type: 'currency-input',
      ...rowCols
    },
    {
      label: 'Invoice Due Date',
      key: 'invoiceDueDate',
      type: 'date',
      ...dueDateCols
    },
    {
      label: 'Status',
      key: 'status',
      type: 'radio',
      options: INVOICE_STATUS_OPTIONS,
      ...statusCols,
      disabledOptions: row => (getInvoiceBalanceDue(row) > 0 ? ['Paid'] : []),
      disabledToastMessage: () => ({
        Paid: 'There must be no balance due before marking this invoice as Paid.'
      })
    }
  ];
};

export const autoFillTaxUtilitiesFromFiles = async ({ reportPropertyAddress = "", value, formValues, setFormValues, orderId, reportId}) => {
  const nextFiles = Array.isArray(value) ? value : [];
  const previousFiles = formValues?.documents?.taxesFiles || [];
  const previousFileIds = previousFiles.map(f => f?.id).filter(Boolean);
  const previousFileIdSet = new Set(previousFileIds);
  const hasNewFiles = nextFiles.some(f => f?.id && !previousFileIdSet.has(f.id));

  setFormValues(prev => ({
    ...prev,
    documents: { ...(prev.documents || {}), taxesFiles: nextFiles }
  }));

  if (!hasNewFiles) return;

  const loadingToastId = toast.loading('Scanning tax bill...');

  try {
    const { response, error } = await makeRESTApiRequest({
      method: 'POST',
      endpoint: ENDPOINTS.REPORT_AUTO_FILL_SECTION,
      requestBody: {
        sectionName: 'taxes',
        files: nextFiles,
        previousFileIds,
        reportPropertyAddress,
        orderId,
        reportId
      }
    });

    if (error) {
      toast.error(error || 'Failed to scan tax bill.');
      return;
    }

    const foundServices = response?.extractedItems || [];
    const extractedCurrentYear = response?.extractedFields?.currentYear
      ? String(response.extractedFields.currentYear)
      : null;

    if (!foundServices.length && !extractedCurrentYear) return;

    let appliedCurrentYear = false;

    setFormValues(current => {
      let next = { ...current };
      const existingCurrentYear = String(next?.taxes?.currentYear || '').trim();

      // Only prefill Current Year when empty — never overwrite an existing value
      if (extractedCurrentYear && !existingCurrentYear) {
        appliedCurrentYear = true;
        next = {
          ...next,
          taxes: {
            ...(next.taxes || {}),
            currentYear: extractedCurrentYear
          }
        };
      }

      if (foundServices.length) {
        const existingServices = next?.utilities?.services || [];
        let services = [...existingServices];

        for (const service of foundServices) {
          const accType = service?.accountType || '';
          const serviceTypes = service?.serviceTypes || [];

          const existingServiceIdx = services.findIndex(s => s?.accountType === accType);
          if (existingServiceIdx >= 0) {
            const existingService = services[existingServiceIdx];
            services[existingServiceIdx] = {
              ...existingService,
              serviceTypes: [...new Set([...(existingService.serviceTypes || []), ...serviceTypes])]
            };
          } else {
            services.push({ accountType: accType, serviceTypes });
          }
        }

        next = {
          ...next,
          utilities: {
            ...(next.utilities || {}),
            services
          }
        };
      }

      return next;
    });

    const messages = [];
    if (appliedCurrentYear) {
      messages.push(`Current Year set to ${extractedCurrentYear}`);
    }
    if (foundServices.length) {
      const names = foundServices.flatMap(s => s?.serviceTypes || []).join(', ');
      messages.push(`Utilities found on Taxes: ${names}`);
    }
    if (messages.length) {
      toast.success(messages.join('. '));
    }
  } catch (err) {
    toast.error(err?.message || 'Failed to scan tax bill.');
  } finally {
    toast.dismiss(loadingToastId);
  }
};

const toInvoiceKey = item => `${item?.invoiceNumber || ''}::${item?.invoiceAmount || ''}`;

export const autoFillInvoicesFromFiles = async ({ reportPropertyAddress = "", value, formValues, setFormValues, reportType = "Lien Search" }) => {
  const nextFiles = Array.isArray(value) ? value : [];
  const previousFiles = formValues?.documents?.invoicesFiles || [];
  const previousFileIds = Array.from(new Set(previousFiles.map(file => file?.id).filter(Boolean)));
  const previousFileIdSet = new Set(previousFileIds);
  const hasNewFiles = nextFiles.some(file => file?.id && !previousFileIdSet.has(file.id));

  const newFormValues = {
    ...formValues,
    documents: {
      ...(formValues.documents || {}),
      invoicesFiles: nextFiles
    }
  };
  setFormValues(newFormValues);

  // Skip auto-fill call when there are no newly added files.
  if (!hasNewFiles) return;

  const loadingToastId = toast.loading('Auto-filling invoice details...');

  try {
    const { response, error } = await makeRESTApiRequest({
      method: 'POST',
      endpoint: ENDPOINTS.REPORT_AUTO_FILL_SECTION,
      requestBody: {
        sectionName: 'invoices',
        files: nextFiles,
        previousFileIds,
        reportPropertyAddress,
        reportType
      }
    });

    if (error) {
      toast.error(error || 'Failed to auto-fill invoice details.');
      return;
    }

    const extractedItems = response?.extractedItems || [];

    setFormValues(current => {
      const currentInvoiceFiles = current?.documents?.invoicesFiles || [];

      const updatedInvoiceFiles = currentInvoiceFiles.map(file => {
        const extracted = extractedItems.find(item => item?.fileId === file?.id);
        return extracted
          ? { ...file, isAddressMatched: extracted.isAddressMatched }
          : file;
      });

      return {
        ...current,
        documents: {
          ...(current.documents || {}),
          invoicesFiles: updatedInvoiceFiles
        }
      };
    });

    if (!extractedItems.length) return;

    setFormValues(current => {
      const currentInvoices = current?.invoices?.invoices || [];
      const existingKeys = new Set(currentInvoices.map(toInvoiceKey));

      const uniqueNewInvoices = extractedItems.filter(item => {
        if (!item?.isAddressMatched) return false;

        const dedupeKey = toInvoiceKey(item);
        if (existingKeys.has(dedupeKey)) return false;
        existingKeys.add(dedupeKey);
        return true;
      });

      if (!uniqueNewInvoices.length) return current;

      const mergedInvoices = [...currentInvoices, ...uniqueNewInvoices];
      const hasAnyFilledInvoiceNumber = mergedInvoices.some(
        item => String(item?.invoiceNumber || '').trim().length > 0
      );
      const normalizedInvoices = hasAnyFilledInvoiceNumber
        ? mergedInvoices.filter(item => String(item?.invoiceNumber || '').trim().length > 0)
        : mergedInvoices;

      return {
        ...current,
        invoices: {
          ...(current.invoices || {}),
          invoices: normalizedInvoices
        }
      };
    });

    toast.success('Invoice details auto-filled from selected files.');
  } catch (err) {
    toast.error(err?.message || 'Failed to auto-fill invoice details.');
  } finally {
    toast.dismiss(loadingToastId);
  }
};


// QA Analysis Side Bar helpers
const LIEN_SEARCH_SECTION_DISPLAY = [
  { key: 'propertyInfo', title: 'PROPERTY INFO' },
  { key: 'taxes', title: 'TAXES' },
  { key: 'permits', title: 'PERMITS' },
  { key: 'codeIssues', title: 'CODE ISSUES' },
  { key: 'specialAssessmentsLiens', title: 'SPECIAL ASSESSMENTS/LIENS' },
  { key: 'utilities', title: 'UTILITIES' },
  { key: 'invoices', title: 'INVOICES' },
  { key: 'manual', title: 'MANUAL' }
];

const ESTOPPEL_SECTION_DISPLAY = [
  { key: 'hoa1', title: 'HOA 1' },
  { key: 'hoa2', title: 'HOA 2' },
  { key: 'hoa3', title: 'HOA 3' },
  { key: 'invoices', title: 'INVOICES' },
  { key: 'manual', title: 'MANUAL' }
];

const getSectionDisplayForOrderType = (orderType = ORDER_TYPES.LIEN_SEARCH) =>
  orderType === ORDER_TYPES.ESTOPPEL ? ESTOPPEL_SECTION_DISPLAY : LIEN_SEARCH_SECTION_DISPLAY;

const getReportQaIssueEndpoints = (orderType = ORDER_TYPES.LIEN_SEARCH) => {
  if (orderType === ORDER_TYPES.ESTOPPEL) {
    return {
      analyze: ENDPOINTS.ESTOPPEL_REPORT_QA_ISSUES_ANALYZE,
      get: ENDPOINTS.ESTOPPEL_REPORT_QA_ISSUES_GET,
      create: ENDPOINTS.ESTOPPEL_REPORT_QA_ISSUES_CREATE,
      update: ENDPOINTS.ESTOPPEL_REPORT_QA_ISSUES_UPDATE,
      validateSupportingFiles: ENDPOINTS.ESTOPPEL_VALIDATE_REPORT_SUPPORTING_FILES
    };
  }

  return {
    analyze: ENDPOINTS.REPORT_QA_ISSUES_ANALYZE,
    get: ENDPOINTS.REPORT_QA_ISSUES_GET,
    create: ENDPOINTS.REPORT_QA_ISSUES_CREATE,
    update: ENDPOINTS.REPORT_QA_ISSUES_UPDATE,
    validateSupportingFiles: ENDPOINTS.VALIDATE_REPORT_SUPPORTING_FILES
  };
};

export const getValidateSupportingFilesEndpoint = (orderType = ORDER_TYPES.LIEN_SEARCH) =>
  getReportQaIssueEndpoints(orderType).validateSupportingFiles;

const formatIssueStatus = (issueType, isManual = false) => {
  if (isManual) return 'Manual';
  if (issueType === 'mismatch') return 'Mismatch';
  if (issueType === 'skipped') return 'Skipped';
  if (issueType === 'missing source') return 'Missing Doc';
  if (issueType === 'missing entry') return 'Missing Value';
  return 'Missing Value';
};

const STATUS_TO_ISSUE_TYPE = {
  Mismatch: 'mismatch',
  'Missing Doc': 'missing source',
  'Missing Value': 'missing entry',
  Skipped: 'skipped',
  Manual: 'missing entry'
};

const formatSourceFiles = sourceFiles =>
  (sourceFiles || [])
    .map(f => f?.fileName)
    .filter(Boolean)
    .join(', ');

/** Human-readable labels for QA issue field keys (mirrors backend reportQa/config/fieldLabels.js). */
const QA_FIELD_LABELS = {
  owner: 'Owner',
  propertyAddress: 'Property Address',
  folio: 'Folio',
  municipality: 'Municipality',
  fileNo: 'File No',
  currentYear: 'Current Year',
  grossAmount: 'Gross Amount',
  totalAmountDue: 'Total Amount Due',
  propertyTaxStatus: 'Property Tax Status',
  propertyDelinquentTax: 'Delinquent Tax',
  propertyDelinquentTaxYears: 'Year(s) NOT PAID',
  tangibleTax: 'Tangible Tax',
  tangibleTaxStatus: 'Tangible Tax Status',
  tangibleTaxAmountDue: 'Tangible Tax Amount Due',
  tangibleDelinquentTax: 'Tangible Delinquent Tax',
  tangibleDelinquentTaxDetails: 'Tangible Delinquent Tax Details',
  businessTax: 'Business Tax',
  businessTaxStatus: 'Business Tax Status',
  businessTaxAmountDue: 'Business Tax Amount Due',
  businessDelinquentTax: 'Business Delinquent Tax',
  businessDelinquentTaxDetails: 'Business Delinquent Tax Details',
  openPermits: 'Open Permits',
  openPermitsInfo: 'Open Permits Info',
  expiredPermits: 'Expired Permits',
  expiredPermitsInfo: 'Expired Permits Info',
  buildingViolations: 'Building Violations',
  buildingViolationsInfo: 'Building Violations Info',
  pendingViolations: 'Pending Violations',
  pendingViolationsCases: 'Pending Violations Cases',
  openInvoices: 'Open Invoices',
  openInvoicesCases: 'Open Invoices Cases',
  specialAssessments: 'Special Assessments',
  specialAssessmentsCollected: 'Special Assessments Collected',
  liens: 'Liens',
  liensInfo: 'Liens Info',
  services: 'Utility Services',
  utilitiesLiens: 'Utility Liens',
  invoices: 'Invoices',
  accountType: 'Account Type',
  provider: 'Provider',
  serviceTypes: 'Services',
  accountNumber: 'Account Number',
  amountDue: 'Amount Due',
  contactNumber: 'Contact Number',
  contactEmail: 'Contact Email',
  mailPaymentTo: 'Mail Payment To',
  reference: 'Reference',
  due: 'Due',
  ownerName: 'Owner Name',
  buyerName: 'Buyer Name',
  hoaName: 'HOA Name',
  associationPhone: 'Association Phone',
  associationEmail: 'Association Email',
  managementCompany: 'Management Company',
  managementCompanyPhone: 'Management Company Phone',
  managementCompanyEmail: 'Management Company Email',
  managementType: 'Management Type',
  associationContacts: 'Association Contacts',
  managementCompanyContacts: 'Management Company Contacts',
  associationDuesDueAmount: 'Association Dues',
  associationDuesType: 'Association Dues Type',
  paidThrough: 'Paid Through',
  hasFeesDueAtClosing: 'Fees Due at Closing',
  feesDueAtClosingDueAmount: 'Fees Due at Closing Amount',
  buyerApprovalRequired: 'Buyer Approval Required',
  specialAssessmentsHasSpecialAssessments: 'Special Assessments',
  specialAssessmentsDueAmount: 'Special Assessments Amount',
  capitalContributionHasCapitalContribution: 'Capital Contribution',
  capitalContributionDueAmount: 'Capital Contribution Amount',
  collections: 'Collections',
  violations: 'Violations'
};

const humanizeFieldSegment = segment => {
  if (!segment) return '';
  if (QA_FIELD_LABELS[segment]) return QA_FIELD_LABELS[segment];
  return segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, char => char.toUpperCase());
};

/** Split paths like `services[0].accountNumber` or `foo.bar` into labeled segments. */
const parseFieldPath = path => {
  const segments = [];
  let cursor = 0;

  while (cursor < path.length) {
    const chunk = path.slice(cursor);
    const match = chunk.match(/^([a-zA-Z_][\w]*)(?:\[(\d+)\])?/);
    if (!match) break;

    const [, name, indexStr] = match;
    segments.push({
      name,
      index: indexStr !== undefined ? Number(indexStr) : null
    });
    cursor += match[0].length;

    if (path[cursor] === '.') {
      cursor += 1;
      continue;
    }
    break;
  }

  return { segments, consumed: cursor === path.length };
};

const formatParsedFieldPath = segments =>
  segments
    .flatMap(segment => {
      const parts = [humanizeFieldSegment(segment.name)];
      if (segment.index !== null) parts.push(String(segment.index + 1));
      return parts;
    })
    .join(' > ');

export const formatQaFieldLabel = fieldKey => {
  const key = String(fieldKey || '').trim();
  if (!key) return '';
  if (QA_FIELD_LABELS[key]) return QA_FIELD_LABELS[key];

  const hasNestedPath = key.includes('.') || key.includes('[');
  if (!hasNestedPath) return humanizeFieldSegment(key);

  const { segments, consumed } = parseFieldPath(key);
  if (!segments.length || !consumed) return key;

  return formatParsedFieldPath(segments);
};

export const formatQaFieldLabels = (fieldNames = []) =>
  fieldNames.map(formatQaFieldLabel).filter(Boolean).join(', ');

export const isCoordinatorManualIssue = (issue = {}) =>
  issue.section === 'manual' && issue.isManual === true;

export const getQaIssueFieldNames = issue => {
  if (issue?.fieldDetails?.length) {
    return issue.fieldDetails.map(detail => String(detail.fieldName || '').trim()).filter(Boolean);
  }
  if (issue?.fieldNames?.length) {
    return issue.fieldNames.map(name => String(name).trim()).filter(Boolean);
  }
  if (issue?.fieldName) {
    return [String(issue.fieldName).trim()].filter(Boolean);
  }
  return [];
};

const MIAMI_TZ = 'America/New_York';
const ISO_DATETIME_WHOLE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
const ISO_DATE_WHOLE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_IN_TEXT =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?/g;
const ISO_DATE_IN_TEXT = /(?<!\d)(\d{4}-\d{2}-\d{2})(?![\dT])/g;

const toMiamiDateOnly = value => {
  if (value == null || value === '') return '';
  const text = String(value).trim();
  if (!text) return '';

  let parsed;
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    parsed = moment(text).tz(MIAMI_TZ);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    parsed = moment.tz(text, 'YYYY-MM-DD', MIAMI_TZ);
  } else {
    parsed = moment(text).tz(MIAMI_TZ);
  }
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
};

/** Format ISO timestamps / YYYY-MM-DD as Miami calendar dates (MM/DD/YYYY, no time). */
export const formatQaHumanDateText = value => {
  if (value == null || value === '') return value;
  const text = String(value);
  const trimmed = text.trim();
  if (!trimmed) return text;

  if (ISO_DATETIME_WHOLE.test(trimmed) || ISO_DATE_WHOLE.test(trimmed)) {
    return toMiamiDateOnly(trimmed) || text;
  }

  if (!/\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return text;
  }

  let next = trimmed.replace(ISO_DATETIME_IN_TEXT, match => toMiamiDateOnly(match) || match);
  next = next.replace(ISO_DATE_IN_TEXT, match => toMiamiDateOnly(match) || match);
  return next;
};

export const formatQaIssueTitle = (issue = {}) => {
  if (isCoordinatorManualIssue(issue)) {
    return issue.description || 'Manual checklist item';
  }

  const issueType = issue.issue;
  const documentType = issue.sourceDocumentType;
  const fieldNames = getQaIssueFieldNames(issue);
  const fieldLabel = formatQaFieldLabels(fieldNames) || '';
  const primaryDetail = (issue.fieldDetails || []).find(
    detail => detail?.expectedValue || detail?.actualValue
  );
  const expected = primaryDetail?.expectedValue
    ? formatQaHumanDateText(String(primaryDetail.expectedValue).trim())
    : '';
  const actual = primaryDetail?.actualValue
    ? formatQaHumanDateText(String(primaryDetail.actualValue).trim())
    : '';

  const withField = phrase => (fieldLabel ? `${fieldLabel}: ${phrase}` : phrase);
  const reportVsSource =
    actual && expected
      ? `report shows "${actual}", source shows "${expected}"`
      : expected
        ? `expected "${expected}"`
        : actual
          ? `report shows "${actual}"`
          : '';

  switch (issueType) {
    case 'mismatch':
      if (fieldLabel && reportVsSource) {
        return `${fieldLabel} mismatch — ${reportVsSource}.`;
      }
      if (fieldLabel) {
        return documentType
          ? `${fieldLabel} does not match ${documentType}.`
          : `${fieldLabel} does not match the source document.`;
      }
      return documentType
        ? `Some values on report do not match ${documentType}.`
        : 'Some values on report do not match the source document.';
    case 'missing entry':
      return fieldLabel
        ? withField(
            documentType
              ? `missing on report but found in ${documentType}.`
              : 'missing on report based on the source documents.'
          )
        : 'Some values are missing based on the documents.';
    case 'missing source':
      return fieldLabel
        ? `${fieldLabel}: source document not attached.`
        : documentType
          ? `${documentType} not attached.`
          : 'Source document not attached.';
    case 'skipped':
      return fieldLabel
        ? documentType
          ? `${fieldLabel} could not be verified against ${documentType}.`
          : `${fieldLabel} could not be verified.`
        : documentType
          ? `Some fields could not be verified against ${documentType}.`
          : 'Some fields could not be verified.';
    default:
      return formatQaHumanDateText(issue.description) || 'Issue detected';
  }
};

const getQaIssueFieldDetails = issue => {
  if (issue?.fieldDetails?.length) {
    return issue.fieldDetails
      .map(detail => ({
        fieldName: String(detail.fieldName || '').trim(),
        expectedValue: detail.expectedValue
          ? formatQaHumanDateText(String(detail.expectedValue).trim())
          : '',
        actualValue: detail.actualValue
          ? formatQaHumanDateText(String(detail.actualValue).trim())
          : '',
        skipReason: detail.skipReason
          ? formatQaHumanDateText(String(detail.skipReason).trim())
          : ''
      }))
      .filter(detail => detail.fieldName);
  }

  return getQaIssueFieldNames(issue).map(fieldName => ({
    fieldName,
    expectedValue: '',
    actualValue: '',
    skipReason: ''
  }));
};

export const buildSectionsFromQaDocument = (qaDocument = {}, orderType = ORDER_TYPES.LIEN_SEARCH) => {
  const sectionDisplay = getSectionDisplayForOrderType(orderType);
  const issues = qaDocument?.issues || [];
  const grouped = Object.fromEntries(sectionDisplay.map(({ key }) => [key, []]));

  issues.forEach((issue, index) => {
    const sectionKey = issue?.section || 'manual';
    if (!grouped[sectionKey]) grouped[sectionKey] = [];
    const isManual = isCoordinatorManualIssue(issue);
    const fieldNames = getQaIssueFieldNames(issue);
    const fieldDetails = getQaIssueFieldDetails(issue);
    const normalizedIssue = { ...issue, fieldNames, fieldDetails };
    grouped[sectionKey].push({
      id:
        issue?._id?.toString() ||
        `${sectionKey}-${index}-${fieldNames.join('-')}-${issue?.issue || ''}`,
      title: isManual ? issue?.description || `Issue ${index + 1}` : formatQaIssueTitle(normalizedIssue),
      description: issue?.description ? formatQaHumanDateText(issue.description) : '',
      file: formatSourceFiles(issue?.sourceFiles),
      status: formatIssueStatus(issue?.issue, isManual),
      severity: issue?.severity,
      isResolved: !!issue?.isResolved,
      isDisliked: !!issue?.isDisliked,
      isManual,
      fieldNames,
      fieldDetails,
      raw: normalizedIssue
    });
  });

  return sectionDisplay.map(({ key, title }) => {
    const sectionIssues = grouped[key] || [];
    const resolvedCount = sectionIssues.filter(i => i.isResolved).length;
    const total = sectionIssues.length;
    return {
      key,
      title,
      count: `${resolvedCount}/${total}`,
      issues: sectionIssues
    };
  });
};

export const getQaSummary = sections => {
  const allIssues = sections.flatMap(s => s.issues);
  const total = allIssues.length;
  const resolved = allIssues.filter(i => i.isResolved).length;
  const aiReviewed = sections
    .filter(s => s.key !== 'manual')
    .reduce((sum, s) => sum + s.issues.length, 0);

  return {
    total,
    resolved,
    progressPercent: total ? Math.round((resolved / total) * 100) : 0,
    aiReviewed
  };
};

export const createEmptySections = (orderType = ORDER_TYPES.LIEN_SEARCH) =>
  buildSectionsFromQaDocument({ issues: [] }, orderType);

// helpers to resolve QA issues analysis and processing
export const resolveReportOrderId = orderId => {
  if (!orderId) return null;
  if (typeof orderId === 'object') return orderId._id || orderId.id || null;
  return orderId;
};

/** Find a row in service.orders by orderId; if id is absent, match orderType instead. */
export const findOrderOnService = (service, orderIdRef, orderType) => {
  const orders = service?.orders ?? [];
  const orderId = resolveReportOrderId(orderIdRef);
  if (orderId) {
    return orders.find(order => String(order._id) === String(orderId));
  }
  if (orderType) {
    return orders.find(order => order.orderType === orderType);
  }
  return undefined;
};

export const fetchReportQaIssuesByOrder = (orderId, orderType = ORDER_TYPES.LIEN_SEARCH) => {
  const endpoints = getReportQaIssueEndpoints(orderType);
  return makeRESTApiRequest({
    endpoint: endpoints.get,
    method: 'POST',
    requestBody: {
      filter: { order: orderId },
      sort: { updatedAt: -1 }
    }
  });
};

export const analyzeReportQaIssues = (orderId, orderType = ORDER_TYPES.LIEN_SEARCH) => {
  const endpoints = getReportQaIssueEndpoints(orderType);
  return makeRESTApiRequest({
    endpoint: endpoints.analyze,
    method: 'POST',
    requestBody: { orderId }
  });
};

export const sectionsToQaIssuesPayload = sections =>
  sections.flatMap(section =>
    section.issues.map(issue => {
      const raw = issue.raw || {};
      const isManual = isCoordinatorManualIssue({
        section: section.key,
        isManual: !!(issue.isManual || raw.isManual)
      });
      const payload = {
        section: section.key,
        issue: raw.issue || STATUS_TO_ISSUE_TYPE[issue.status] || 'missing entry',
        sourceFiles: raw.sourceFiles || [],
        isResolved: !!issue.isResolved,
        isDisliked: !!issue.isDisliked,
        isManual
      };
      if (isManual) {
        payload.description = issue.title;
      } else if (issue.description || raw.description) {
        payload.description = issue.description || raw.description;
      }
      if (issue.severity || raw.severity) payload.severity = issue.severity || raw.severity;
      if (raw.sourceDocumentType) payload.sourceDocumentType = raw.sourceDocumentType;
      if (issue.fieldDetails?.length) payload.fieldDetails = issue.fieldDetails;
      else if (raw.fieldDetails?.length) payload.fieldDetails = raw.fieldDetails;
      if (issue.fieldNames?.length) payload.fieldNames = issue.fieldNames;
      else if (raw.fieldNames?.length) payload.fieldNames = raw.fieldNames;
      if (raw._id) payload._id = raw._id;
      return payload;
    })
  );

export const saveReportQaIssues = async ({
  qaDocumentId,
  orderId,
  sections,
  orderType = ORDER_TYPES.LIEN_SEARCH
}) => {
  const issues = sectionsToQaIssuesPayload(sections);
  const endpoints = getReportQaIssueEndpoints(orderType);

  if (qaDocumentId) {
    return makeRESTApiRequest({
      endpoint: endpoints.update(qaDocumentId),
      method: 'POST',
      requestBody: { issues }
    });
  }

  if (!orderId) {
    return { response: null, error: 'Order is required to save QA issues.' };
  }

  return makeRESTApiRequest({
    endpoint: endpoints.create,
    method: 'POST',
    requestBody: { order: orderId, issues }
  });
};

export const countAiQaIssues = (sections = []) =>
  sections.filter(s => s.key !== 'manual').reduce((sum, s) => sum + s.issues.length, 0);

export const fetchReportQaSectionsByOrder = async (orderId, orderType = ORDER_TYPES.LIEN_SEARCH) => {
  const { response, error } = await fetchReportQaIssuesByOrder(orderId, orderType);
  if (error) return { sections: createEmptySections(orderType), qaDocumentId: null, qaDocument: null, error };

  const qaDocument = Array.isArray(response) ? response[0] : response;
  return {
    sections: buildSectionsFromQaDocument(qaDocument || { issues: [] }, orderType),
    qaDocumentId: qaDocument?._id || null,
    qaDocument: qaDocument || null,
    error: null
  };
};

const LIEN_SEARCH_QA_PROPERTY_INFO_FIELDS = new Set([
  'owner',
  'propertyAddress',
  'folio',
  'municipality',
  'fileNo'
]);

const ESTOPPEL_QA_PROPERTY_INFO_FIELDS = new Set([
  'ownerName',
  'buyerName',
  'propertyAddress',
  'folio',
  'fileNo'
]);

const LIEN_SEARCH_QA_DOCUMENT_FILE_KEYS = new Set([
  'invoicesFiles',
  'taxesFiles',
  'permitsFiles',
  'codeIssuesFiles',
  'specialAssessmentsLiensFiles',
  'utilitiesFiles'
]);

const ESTOPPEL_QA_DOCUMENT_FILE_KEYS = new Set(['invoicesFiles', 'hoa1Files', 'hoa2Files', 'hoa3Files']);

const LIEN_SEARCH_QA_SECTION_TO_STEP = {
  propertyInfo: null,
  taxes: 'taxes',
  utilities: 'utilities',
  permits: 'permits',
  codeIssues: 'codeIssues',
  specialAssessmentsLiens: 'specialAssessmentsLiens',
  invoices: 'invoices'
};

const ESTOPPEL_QA_SECTION_TO_STEP = {
  hoa1: 'hoa1',
  hoa2: 'hoa2',
  hoa3: 'hoa3',
  invoices: 'invoices'
};

const QA_NAVIGATION_BY_ORDER_TYPE = {
  [ORDER_TYPES.LIEN_SEARCH]: {
    propertyInfoFields: LIEN_SEARCH_QA_PROPERTY_INFO_FIELDS,
    documentFileKeys: LIEN_SEARCH_QA_DOCUMENT_FILE_KEYS,
    sectionToStep: LIEN_SEARCH_QA_SECTION_TO_STEP
  },
  [ORDER_TYPES.ESTOPPEL]: {
    propertyInfoFields: ESTOPPEL_QA_PROPERTY_INFO_FIELDS,
    documentFileKeys: ESTOPPEL_QA_DOCUMENT_FILE_KEYS,
    sectionToStep: ESTOPPEL_QA_SECTION_TO_STEP
  }
};

const getQaNavigationConfig = (orderType = ORDER_TYPES.LIEN_SEARCH) =>
  QA_NAVIGATION_BY_ORDER_TYPE[orderType] || QA_NAVIGATION_BY_ORDER_TYPE[ORDER_TYPES.LIEN_SEARCH];

export const buildQaFieldIssueMap = sections => {
  const map = {};
  sections.forEach(section => {
    section.issues.forEach(issue => {
      if (issue.isResolved) return;
      const fieldNames = issue.fieldNames?.length
        ? issue.fieldNames
        : (issue.fieldDetails || []).map(detail => detail.fieldName).filter(Boolean);
      fieldNames.forEach(fieldKey => {
        map[fieldKey] = {
          status: issue.status,
          section: section.key,
          issueId: issue.id
        };
      });
    });
  });
  return map;
};

// Form step keys that have at least one unresolved QA issue (for tab indicators).
export const buildUnresolvedQaIssueStepKeys = (sections = [], orderType = ORDER_TYPES.LIEN_SEARCH) => {
  const { documentFileKeys, sectionToStep } = getQaNavigationConfig(orderType);
  const stepKeys = new Set();

  sections.forEach(section => {
    const hasUnresolved = section.issues.some(issue => !issue.isResolved);
    if (!hasUnresolved) return;

    const stepKey = sectionToStep[section.key];
    if (stepKey) stepKeys.add(stepKey);
  });

  sections.forEach(section => {
    section.issues.forEach(issue => {
      if (issue.isResolved) return;
      const fieldNames = issue.fieldNames?.length
        ? issue.fieldNames
        : (issue.fieldDetails || []).map(detail => detail.fieldName).filter(Boolean);
      fieldNames.forEach(fieldKey => {
        if (documentFileKeys.has(fieldKey)) {
          stepKeys.add('documents');
        }
      });
    });
  });

  return stepKeys;
};

export const resolveQaIssueNavigation = ({
  sectionKey,
  fieldNames = [],
  orderType = ORDER_TYPES.LIEN_SEARCH
}) => {
  const { propertyInfoFields, documentFileKeys, sectionToStep } = getQaNavigationConfig(orderType);
  const fieldKey = fieldNames[0];
  if (!fieldKey) return null;

  if (propertyInfoFields.has(fieldKey) || sectionKey === 'propertyInfo') {
    return { stepKey: null, fieldKey };
  }

  if (documentFileKeys.has(fieldKey)) {
    return { stepKey: 'documents', fieldKey };
  }

  const stepKey = sectionToStep[sectionKey];
  if (stepKey) return { stepKey, fieldKey };

  return { stepKey: null, fieldKey };
};

export const qaIssueStatusToFieldTone = status => {
  if (status === 'Missing Doc' || status === 'Missing Value' || status === 'Missing') {
    return 'warning';
  }
  if (status === 'Skipped') return 'default';
  if (status) return 'error';
  return null;
};

export const buildFieldStatusMapFromQaIssues = qaFieldIssues => {
  const map = {};
  Object.entries(qaFieldIssues || {}).forEach(([fieldKey, issue]) => {
    const tone = qaIssueStatusToFieldTone(issue?.status);
    if (tone) map[fieldKey] = tone;
  });
  return map;
};

export const scrollToFieldKey = fieldKey => {
  if (!fieldKey) return;

  setTimeout(() => {
    const field = document.querySelector(`[data-field-key="${fieldKey}"]`);
    if (field) field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 250);
};

const AUTO_ORG_DOCUMENT_TABS = [
  'taxes',
  'permits',
  'codeIssues',
  'specialAssessmentsLiens',
  'utilities',
  'invoices'
];

const mergeDriveFileLists = (local = [], remote = []) => {
  const seen = new Set((local || []).map(file => String(file?.id || file?.fileId || '')).filter(Boolean));
  const merged = [...(local || [])];
  for (const file of remote || []) {
    const id = String(file?.id || file?.fileId || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(file);
  }
  return merged;
};

const mapInvoiceRowFromReport = inv => ({
  ...(inv?._id != null && { _id: String(inv._id) }),
  ...(inv?.serviceVersionId != null && { serviceVersionId: String(inv.serviceVersionId) }),
  invoiceNumber: inv?.invoiceNumber ?? '',
  invoiceAmount: inv?.invoiceAmount ?? '',
  paymentReceived: inv?.paymentReceived ?? '',
  invoiceDueDate: inv?.invoiceDueDate ?? '',
  status: inv?.status || 'Unpaid'
});

/** Merge newly attached Drive files into report builder form state without wiping user edits. */
export const mergeReportAutoOrganizationFiles = (prev, report) => {
  if (!prev || !report) return prev;

  const next = { ...prev };
  const documents = { ...(prev.documents || {}) };

  for (const key of AUTO_ORG_DOCUMENT_TABS) {
    const remoteFiles = report[key]?.files;
    if (!Array.isArray(remoteFiles) || !remoteFiles.length) continue;
    const mergedFiles = mergeDriveFileLists(prev[key]?.files, remoteFiles);
    next[key] = { ...(next[key] || {}), files: mergedFiles };
    documents[`${key}Files`] = mergeDriveFileLists(documents[`${key}Files`], remoteFiles);
  }

  next.documents = documents;
  return next;
};

/** After auto-organization finishes, also merge tax year / utilities / invoice auto-fill. */
export const mergeReportAutoFillFromOrganization = (prev, report) => {
  const next = mergeReportAutoOrganizationFiles(prev, report);
  if (!next || !report) return prev;

  const remoteYear = report?.taxes?.currentYear;
  if (remoteYear != null && (next.taxes?.currentYear == null || String(next.taxes.currentYear).trim() === '')) {
    next.taxes = { ...next.taxes, currentYear: remoteYear };
  }

  if (Array.isArray(report?.utilities?.services) && report.utilities.services.length) {
    const services = (next.utilities?.services || []).map(service => ({ ...service }));
    for (const item of report.utilities.services) {
      const accountType = item?.accountType || '';
      const serviceTypes = item?.serviceTypes || [];
      if (!accountType) continue;
      const idx = services.findIndex(service => service?.accountType === accountType);
      if (idx >= 0) {
        services[idx] = {
          ...services[idx],
          serviceTypes: [...new Set([...(services[idx].serviceTypes || []), ...serviceTypes])]
        };
      } else {
        services.push({ accountType, serviceTypes });
      }
    }
    next.utilities = { ...next.utilities, services };
  }

  if (Array.isArray(report?.invoices?.invoices) && report.invoices.invoices.length) {
    const existing = [...(next.invoices?.invoices || [])];
    const keyOf = inv => `${inv?.invoiceNumber || ''}::${inv?.invoiceAmount || ''}`;
    const seen = new Set(existing.map(keyOf));
    const additions = [];
    for (const inv of report.invoices.invoices) {
      if (!String(inv?.invoiceNumber || '').trim()) continue;
      const key = keyOf(inv);
      if (seen.has(key)) continue;
      seen.add(key);
      additions.push(mapInvoiceRowFromReport(inv));
    }
    if (additions.length) {
      next.invoices = { ...next.invoices, invoices: [...existing, ...additions] };
    }
  }

  return next;
};

